import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import StatusBadge from '../../components/ui/StatusBadge';
import CombinedCommentsWidget from '../../components/dashboard/CombinedCommentsWidget';
import CapacityBar from '../../components/ui/CapacityBar';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../firebase/config';
import DailyRoutinesWidget from '../../components/dashboard/DailyRoutinesWidget';
import { Trash2 } from 'lucide-react';
import { 
  Users, 
  FolderOpen, 
  CheckSquare, 
  AlertTriangle,
  Clock,
  Calendar,
  X,
  Plus,
  FileText,
  Globe,
  Building2,
  User,
  Edit,
  AlertCircle,
  RotateCcw,
  Activity,
  Sun,
  Moon
} from 'lucide-react';
import TabsComponent from '../../components/ui/TabsComponent';
import { useNavigate } from 'react-router-dom';

import { renderTasksContent, renderProjectsContent, renderTeamsContent, renderDepartmentsContent } from '../../components/dashboard/InlineContent';

const DeptManagerDashboard = ({ activeTab, setActiveTab }) => {
  const { currentUser } = useAuth();
  const { tasks = [], projects = [], users = [], departments = [], teams = [],
          updateProject, updateTask, updateUser, createProject, createTask, deleteProject, deleteTask,
          createTeam, updateTeam, deleteTeam } = useData();
  const navigate = useNavigate();

  // State for filters
  const [filterProjectStatus, setFilterProjectStatus] = useState('all');
  const [filterTaskStatus, setFilterTaskStatus] = useState('all');
  const [filterTaskPriority, setFilterTaskPriority] = useState('all');
  const [filterProjectScope, setFilterProjectScope] = useState('all');
  const [showCompletedProjects, setShowCompletedProjects] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  
  // State for inline content display
  const [selectedContent, setSelectedContent] = useState(null); // 'tasks', 'projects', 'teams', 'departments'
  const [selectedTab, setSelectedTab] = useState('management'); // 'management', 'personal'
  useEffect(() => {
    setSelectedContent(null);
    setSelectedTab(activeTab);
  }, [activeTab]);




  // Function to handle box clicks
  const handleBoxClick = (contentType, tabType) => {
    setSelectedContent(contentType);
    setSelectedTab(tabType);
  };

  // Render inline content based on selection
 const renderInlineContent = () => {
  if (!selectedContent) return null;

  const isPersonal = selectedTab === 'personal';
  
  switch (selectedContent) {
    case 'tasks':
      const filteredTasks = isPersonal 
        ? tasks.filter(t => 
            t.scope === 'personal' && 
            (t.assigneeId === currentUser?.id || t.createdBy === currentUser?.id)
          )
        : tasks.filter(t => 
            t.departmentId === currentUser?.departmentId && 
            t.scope !== 'personal' && 
            !shouldHideTask(t)
          );
      return renderTasksContent(filteredTasks, users, currentUser, isPersonal, setSelectedContent, navigate);
      
    case 'projects':
      const filteredProjects = isPersonal
        ? projects.filter(p => 
            p.scope === 'personal' && 
            (p.assigneeId === currentUser?.id || p.createdBy === currentUser?.id)
          )
        : projects.filter(p => 
            p.departmentId === currentUser?.departmentId && 
            p.scope !== 'personal'
          );
      return renderProjectsContent(filteredProjects, tasks, users, currentUser, isPersonal, setSelectedContent, navigate);
      
    case 'teams':
      if (isPersonal) return null;
      return renderTeamsContent(teams, users, tasks, setSelectedContent, navigate, currentUser);
      
    case 'departments':
      if (isPersonal) return null;
      return renderDepartmentsContent(departments, users, tasks, setSelectedContent, navigate);
      
    default:
      return null;
  }
};

  // State for modals
  const [showEditProject, setShowEditProject] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [editTeam, setEditTeam] = useState(null);

  // State for team member modal
  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [addToAllDepartments, setAddToAllDepartments] = useState(false);

  // State for new forms
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    scope: 'department',
    priority: 'important_not_urgent',
    startDate: '',
    endDate: '',
    teamMembers: []
  });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigneeId: '',
    priority: 'important_not_urgent',
    dueDate: '',
    estimate: 0,
    projectId: ''
  });

  // Get department projects for task creation
  const departmentProjects = projects.filter(p => p.departmentId === currentUser?.departmentId);

  // State for new team form
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    departmentId: currentUser?.departmentId || '',
    managerId: '',
    memberIds: [],
    isActive: true
  });

  // Helper functions
  const shouldHideTask = (task) => {
    // הסתר משימות שנוצרו משגרות יומיות - הן צריכות להופיע רק בעמוד השגרות
    if (task.isDailyRoutine || task.routineId) {
      return true;
    }
    
    const taskProject = task.projectId ? projects.find(p => p.id === task.projectId) : null;
    const hidePersonalTask = task.scope === 'personal' && task.assigneeId !== currentUser?.id && task.createdBy !== currentUser?.id;
    const hidePersonalProjectTask = taskProject && taskProject.scope === 'personal' && taskProject.assigneeId !== currentUser?.id && taskProject.createdBy !== currentUser?.id;
    return hidePersonalTask || hidePersonalProjectTask;
  };

  const getProjectStatus = (project) => {
    if (!project) return 'לא ידוע';
    const projectTasks = tasks.filter(t => t.projectId === project.id);
    if (projectTasks.length === 0) return 'ממתין לעבודה';
    const completedTasks = projectTasks.filter(t => t.status === 'הושלם').length;
    const totalTasks = projectTasks.length;
    if (completedTasks === 0) return 'ממתין לעבודה';
    if (completedTasks === totalTasks) return 'פרויקט הושלם';
    return 'פרויקט בתהליך';
  };

  // Filter projects and tasks
  const filteredDashboardProjects = projects.filter(project => {
    let hasAccess = false;
    
    if (currentUser?.role === 'dept_manager') {
      // מנהל מחלקה רואה פרויקטים מהמחלקה שלו
      hasAccess = project.departmentId === currentUser.departmentId;
    }
    
    if (!hasAccess) return false;
    
    const matchesScope = filterProjectScope === 'all' || project.scope === filterProjectScope;
    const projectStatus = getProjectStatus(project);
    const matchesStatus = filterProjectStatus === 'all' || 
                         (filterProjectStatus === 'completed' && projectStatus === 'פרויקט הושלם') ||
                         projectStatus === filterProjectStatus;
    
    if (showCompletedProjects && projectStatus !== 'פרויקט הושלם') return false;
    if (!showCompletedProjects && projectStatus === 'פרויקט הושלם') return false;
    
    return matchesScope && matchesStatus;
  });

  const filteredDashboardTasks = tasks.filter(task => {
    if (shouldHideTask(task)) return false;
    const matchesStatus = filterTaskStatus === 'all' || task.status === filterTaskStatus;
    const matchesPriority = filterTaskPriority === 'all' || task.priority === filterTaskPriority;
    if (showCompletedTasks && task.status !== 'הושלם') return false;
    if (!showCompletedTasks && task.status === 'הושלם') return false;
    return matchesStatus && matchesPriority;
  });

  // Stats card component
  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold mt-1">{value || 0}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    );
  };

  // Function to add team member to team
  const handleAddTeamMember = async () => {
    if (!selectedUserToAdd) return;
    
    try {
      const selectedUser = users.find(u => u.id === selectedUserToAdd);
      if (selectedUser?.teamId) {
        alert('העובד כבר משויך לצוות אחר!');
        return;
      }
      
      await updateUser(selectedUserToAdd, { 
        teamId: currentUser.id,
        teamManagerId: currentUser.id 
      });
      
      alert('העובד נוסף לצוות בהצלחה!');
      
      setShowAddTeamMemberModal(false);
      setSelectedUserToAdd('');
      setSelectedDepartmentId('');
      setAddToAllDepartments(false);
      
    } catch (error) {
      console.error('Error adding team member:', error);
      alert('שגיאה בהוספת העובד לצוות');
    }
  };

  // Function to create new project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...newProject,
        departmentId: currentUser.departmentId,
        createdBy: currentUser.id,
        createdAt: new Date(),
        status: 'active'
      };
      
      await createProject(projectData);
      setShowNewProjectModal(false);
      setShowNewProject(false);
      setNewProject({
        name: '',
        description: '',
        scope: 'department',
        priority: 'important_not_urgent',
        startDate: '',
        endDate: '',
        teamMembers: []
      });
      alert('הפרויקט נוצר בהצלחה!');
    } catch (error) {
      console.error('Error creating project:', error);
      alert('שגיאה ביצירת הפרויקט');
    }
  };

  // Function to create new task
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        ...newTask,
        departmentId: currentUser.departmentId,
        createdBy: currentUser.id,
        createdAt: new Date(),
        status: 'במסלול',
        projectId: newTask.projectId || null
      };
      
      await createTask(taskData);
      setShowNewTaskModal(false);
      setShowNewTask(false);
      setNewTask({
        title: '',
        description: '',
        assigneeId: '',
        priority: 'important_not_urgent',
        dueDate: '',
        estimate: 0,
        projectId: ''
      });
      alert('המשימה נוצרה בהצלחה!');
    } catch (error) {
      console.error('Error creating task:', error);
      alert('שגיאה ביצירת המשימה');
    }
  };

  // Tabs configuration
  const tabs = (() => {
    const managementTab = {
      id: 'management',
      label: 'ניהול',
      icon: Building2,
      badge: departments.length.toString(),
      content: (
        <div className="p-6">
         <h1 className="text-2xl font-bold mb-6">לוח בקרה - מנהל מחלקה</h1>

{/* כותרת מרכזית */}
<div className="text-center mb-6">
  <h2 className="text-3xl font-bold mb-2">ניהול המחלקה שלי</h2>
  <div className="inline-block px-6 py-3 bg-blue-100 border-2 border-blue-300 rounded-xl">
    <div className="flex items-center gap-3">
      <Building2 size={28} className="text-blue-700" />
      <span className="text-2xl font-bold text-blue-900">
        {departments.find(d => d.id === currentUser?.departmentId)?.name || 'לא משויך למחלקה'}
      </span>
    </div>
  </div>
</div>

{/* 4 הכרטיסיות */}
<div className="bg-white rounded-lg shadow p-8 mb-8">
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
    
    {/* כרטיסיית המחלקה שלי */}
    <div className="border rounded-lg p-8 hover:bg-gray-50 cursor-pointer" 
         onClick={() => handleBoxClick('departments', 'management')}>
      <div className="text-center">
        <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
  <Building2 size={40} className="text-blue-600" />
</div>
       <h3 className="text-xl font-semibold mb-3">המחלקה שלי</h3>
<p className="text-4xl font-bold text-blue-600 mb-3">1</p>
        <p className="text-sm text-gray-600">מחלקה פעילה</p>
      </div>
    </div>

    {/* כרטיסיית פרויקטי המחלקה */}
    <div className="border rounded-lg p-8 hover:bg-gray-50 cursor-pointer" 
         onClick={() => handleBoxClick('projects', 'management')}>
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <FolderOpen size={32} className="text-green-600" />
        </div>
        <h3 className="text-lg font-semibold mb-2">פרויקטי המחלקה</h3>
       <p className="text-3xl font-bold text-green-600 mb-2">
  {projects.filter(p => 
    p.departmentId === currentUser?.departmentId && 
    p.scope !== 'personal'
  ).length}
</p>
        <p className="text-sm text-gray-600">פרויקטים פעילים</p>
      </div>
    </div>

    {/* כרטיסיית משימות המחלקה */}
    <div className="border rounded-lg p-8 hover:bg-gray-50 cursor-pointer" 
         onClick={() => handleBoxClick('tasks', 'management')}>
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
          <CheckSquare size={32} className="text-purple-600" />
        </div>
        <h3 className="text-lg font-semibold mb-2">משימות המחלקה</h3>
     <p className="text-3xl font-bold text-purple-600 mb-2">
  {tasks.filter(t => 
    t.departmentId === currentUser?.departmentId && 
    t.scope !== 'personal' &&
    !shouldHideTask(t)
  ).length}
</p>
        <p className="text-sm text-gray-600">משימות פעילות</p>
      </div>
    </div>

    {/* כרטיסיית צוותי המחלקה */}
    <div className="border rounded-lg p-8 hover:bg-gray-50 cursor-pointer" 
         onClick={() => handleBoxClick('teams', 'management')}>
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
          <Users size={32} className="text-orange-600" />
        </div>
        <h3 className="text-lg font-semibold mb-2">צוותי המחלקה</h3>
        <p className="text-3xl font-bold text-orange-600 mb-2">
          {teams.filter(t => t.departmentId === currentUser?.departmentId).length}
        </p>
        <p className="text-sm text-gray-600">צוותים פעילים</p>
      </div>
    </div>

  </div>
</div>
      {/* Inline Content Display */}
{renderInlineContent()}
          {/* Department Info */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">המחלקות שלי</h2>
              <button
                onClick={() => setShowAddTeamMemberModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus size={16} />
                הוסף עובד
              </button>
            </div>
            
            {/* Show only my department for department managers */}
            {(() => {
              const myDepartments = departments.filter(d => {
                // מנהל מחלקה רואה רק את המחלקה שלו
                if (currentUser?.role === 'dept_manager') {
                  return d.id === currentUser.departmentId;
                }
                // תפקידים אחרים (למקרה שהקוד משתמש במקום אחר)
                return d.id === currentUser.departmentId || 
                       d.managerId === currentUser.id || 
                       d.createdBy === currentUser.id;
              });
              
              return (
                <div className="space-y-4">
                  {myDepartments.length === 0 ? (
                    <div className="text-center py-8">
                      <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600">לא משויך למחלקה כלשהי</p>
                      <p className="text-sm text-gray-500 mt-2">פנה למנהל המערכת לצורך שיוך למחלקה</p>
                    </div>
                  ) : (
                    myDepartments.map(dept => (
                    <div key={dept.id} className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/departments/${dept.id}`)}>
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Users size={24} className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{dept.name}</h3>
                        <p className="text-gray-600">{dept.description || 'אין תיאור'}</p>
                        
                        {/* Manager Info */}
                        {(() => {
                          const managerIds = dept.managerIds || (dept.managerId ? [dept.managerId] : []);
                          if (managerIds.length === 0) return null;
                          
                          const managerNames = managerIds.map(id =>
                            users.find(u => u.id === id)?.name || 'לא ידוע'
                          ).join(', ');
                          
                          return (
                            <p className="text-sm text-blue-600 mt-1">
                              <span className="font-medium">מנהלים: </span>
                              {managerNames}
                            </p>
                          );
                        })()}
                        
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-1 rounded text-xs ${dept.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {dept.isActive ? 'פעילה' : 'לא פעילה'}
                          </span>
                          {dept.managerId === currentUser.id && (
                            <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                              מנהל
                            </span>
                          )}
                          {dept.createdBy === currentUser.id && (
                            <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">
                              יוצר
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                  )}
                </div>
              );
            })()}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {(() => {
              const teamMembers = users.filter(u => 
                u.departmentId === currentUser.departmentId && u.id !== currentUser.id
              );
              const teamTasks = filteredDashboardTasks.filter(t => t.departmentId === currentUser.departmentId && !shouldHideTask(t));
              
              return (
                <>
                  <StatCard 
                    title="חברי צוות" 
                    value={teamMembers.length} 
                    icon={Users} 
                    color="blue"
                  />
                  <StatCard 
                    title="משימות פעילות" 
                    value={teamTasks.filter(t => t.status !== 'הושלם').length} 
                    icon={CheckSquare} 
                    color="green"
                  />
                  <StatCard 
                    title="לא אושרו" 
                    value={teamTasks.filter(t => 
                      !t.acknowledged && 
                      teamMembers.some(m => 
                        t.assigneeId === m.id || 
                        (Array.isArray(t.assigneeIds) && t.assigneeIds.includes(m.id))
                      )
                    ).length} 
                    icon={Clock} 
                    color="yellow"
                  />
                  <StatCard 
                    title="בסיכון/חסום" 
                    value={teamTasks.filter(t => 
                      t.status === 'בסיכון' || t.status === 'חסום'
                    ).length} 
                    icon={AlertTriangle} 
                    color="red"
                  />
                </>
              );
            })()}
          </div>
          
{/* Daily Routines Section */}
<DailyRoutinesWidget />
{/* הערות חדשות */}
          <CombinedCommentsWidget />

        {/* Day Opening & Summary Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-purple-500 rounded-lg flex items-center justify-center">
                <Sun className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">פתיחה וסיכום יום</h2>
                <p className="text-sm text-gray-600">נהל את היום שלך בצורה מסודרת</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/day-opening')}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2 text-sm font-medium"
              >
                <Sun className="w-4 h-4" />
                פתיחת יום
              </button>
              <button
                onClick={() => navigate('/day-summary')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium"
              >
                <Moon className="w-4 h-4" />
                סיכום יום
              </button>
              <button
                onClick={() => navigate('/team-summaries')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
              >
                <Users className="w-4 h-4" />
                סיכומי המחלקה
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sun className="w-5 h-5 text-orange-600" />
                <h3 className="font-semibold text-orange-800">פתיחת יום</h3>
              </div>
              <p className="text-sm text-orange-700">
                תכנן את היום: יעדים, משימות מרכזיות, פגישות וחסמים צפויים
              </p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Moon className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-purple-800">סיכום יום</h3>
              </div>
              <p className="text-sm text-purple-700">
                סכם את היום: הישגים, אתגרים, תובנות ותכנון למחר
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-blue-800">סיכומי המחלקה</h3>
              </div>
              <p className="text-sm text-blue-700">
                צפה בפתיחות וסיכומי יום של כל הצוותים במחלקה
              </p>
            </div>
          </div>
        </div>

        {/* Dashboard Filters */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">פילטרים ללוח הבקרה</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Project Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">סטטוס פרויקטים</label>
                <select
                  value={filterProjectStatus}
                  onChange={(e) => setFilterProjectStatus(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">כל הסטטוסים</option>
                  <option value="ממתין לעבודה">פרויקט חדש- ממתין לעבודה</option>
                  <option value="פרויקט בתהליך">פרויקט בתהליך</option>
                  <option value="פרויקט הושלם">פרויקט הושלם</option>
                </select>
              </div>

              {/* Project Scope Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">תחום פרויקטים</label>
                <select
                  value={filterProjectScope}
                  onChange={(e) => setFilterProjectScope(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">כל התחומים</option>
                  <option value="personal">אישי</option>
                  <option value="managerial">ניהולי</option>
                </select>
              </div>

              {/* Task Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">סטטוס משימות</label>
                <select
                  value={filterTaskStatus}
                  onChange={(e) => setFilterTaskStatus(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">כל הסטטוסים</option>
                  <option value="ממתין לעבודה">משימה חדשה- ממתינה לעבודה</option>
                  <option value="במסלול">משימה בתהליך</option>
                  <option value="הושלם">משימה הושלמה</option>
                </select>
              </div>

              {/* Task Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">עדיפות משימות</label>
                <select
                  value={filterTaskPriority}
                  onChange={(e) => setFilterTaskPriority(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">כל העדיפויות</option>
                  <option value="important_urgent">חשוב ודחוף</option>
                  <option value="important_not_urgent">חשוב ולא דחוף</option>
                  <option value="not_important_urgent">דחוף ולא חשוב</option>
                  <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                </select>
              </div>
            </div>

            {/* Toggle Buttons */}
            <div className="flex flex-wrap gap-3 mt-4">
              <button
                onClick={() => setShowCompletedProjects(!showCompletedProjects)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showCompletedProjects 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showCompletedProjects ? 'הסתר פרויקטים שהושלמו' : 'הצג פרויקטים שהושלמו'}
              </button>

              <button
                onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showCompletedTasks 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showCompletedTasks ? 'הסתר משימות שהושלמו' : 'הצג משימות שהושלמו'}
              </button>
            </div>
          </div>

          {/* Team Members Load */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold">עומס עבודה - חברי הצוות</h2>
            </div>
            <div className="p-6 space-y-4">
              {(() => {
                const teamMembers = users.filter(u => 
                  u.departmentId === currentUser.departmentId && u.id !== currentUser.id
                );
                const teamTasks = filteredDashboardTasks.filter(t => t.departmentId === currentUser.departmentId && !shouldHideTask(t));
                
                return teamMembers.map(member => {
                  const memberTasks = teamTasks.filter(t => 
                    (t.assigneeId === member.id || 
                     (Array.isArray(t.assigneeIds) && t.assigneeIds.includes(member.id))) && 
                    t.status !== 'הושלם'
                  );
                  const totalEstimate = memberTasks.reduce((sum, t) => sum + (t.estimate || 0), 0);

                  return (
                    <div key={member.id}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{member.name}</span>
                        <span className="text-sm text-gray-600">
                          {memberTasks.length} משימות פעילות
                        </span>
                      </div>
                      <CapacityBar 
                        current={totalEstimate} 
                        total={member.weeklyCapacity || 40}
                        showLabel={true}
                      />
                    </div>
                  );
                });
              })()}
            </div>
          </div>

        

          
         
        </div>
      )
    };

    const personalTab = {
      id: 'personal',
      label: 'אישי',
      icon: User,
 badge: (() => {
    const personalProjects = projects.filter(p => 
        p.scope === 'personal' && 
        (p.assigneeId === currentUser.id || p.createdBy === currentUser.id)
    );
    const personalTasks = tasks.filter(t => 
        t.scope === 'personal' && 
        (t.assigneeId === currentUser.id || t.createdBy === currentUser.id)
    );
    return (personalProjects.length + personalTasks.length).toString();
})(),
      content: (
        <div className="p-6">
         <h1 className="text-2xl font-bold mb-6 text-center">לוח בקרה - מנהל מחלקה</h1>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="border-b pb-4 mb-4">
              <h2 className="text-lg font-semibold">פרויקטים ומשימות אישיים</h2>
              <p className="text-sm text-gray-600 mt-1">ניהול הפרויקטים והמשימות האישיים שלך</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Projects Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטים אישיים</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">
                    {(() => {
                      const personalProjects = projects.filter(p => p.scope === 'personal');
                      return personalProjects.length;
                    })()}
                  </p>
                  <p className="text-sm text-gray-600">פרויקטים אישיים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const personalProjects = projects.filter(p => p.scope === 'personal');
                      const activeProjects = personalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length === 0 || done < pts.length;
                      });
                      const completedProjects = personalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length > 0 && done === pts.length;
                      });
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeProjects.length} פעילים
                          </div>
                          <div className="text-center py-1 text-blue-600">
                            {completedProjects.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Personal Tasks Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">משימות אישיות</h3>
             <p className="text-3xl font-bold text-purple-600 mb-2">
  {(() => {
    const personalTasks = tasks.filter(t => 
      t.scope === 'personal' && 
      (t.assigneeId === currentUser.id || t.createdBy === currentUser.id)
    );
    return personalTasks.length;
  })()}
</p>
                  <p className="text-sm text-gray-600">משימות אישיות</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const personalTasks = tasks.filter(t => t.scope === 'personal');
                      const activeTasks = personalTasks.filter(t => t.status !== 'הושלם');
                      const completedTasks = personalTasks.filter(t => t.status === 'הושלם');
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeTasks.length} פעילות
                          </div>
                          <div className="text-center py-1 text-purple-600">
                            {completedTasks.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
            {/* Inline Content Display */}
{renderInlineContent()}

{/* Daily Routines Section */}
<DailyRoutinesWidget />

{/* הערות חדשות */}
<CombinedCommentsWidget />

{/* Day Opening & Summary Section */}
<div className="bg-white rounded-lg shadow p-6 mb-6 mt-6">
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-purple-500 rounded-lg flex items-center justify-center">
        <Sun className="w-5 h-5 text-white" />
      </div>
      <div>
        <h2 className="text-lg font-semibold">פתיחה וסיכום יום</h2>
        <p className="text-sm text-gray-600">נהל את היום שלך בצורה מסודרת</p>
      </div>
    </div>
    <div className="flex gap-2">
      <button
        onClick={() => navigate('/day-opening')}
        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2 text-sm font-medium"
      >
        <Sun className="w-4 h-4" />
        פתיחת יום
      </button>
      <button
        onClick={() => navigate('/day-summary')}
        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium"
      >
        <Moon className="w-4 h-4" />
        סיכום יום
      </button>
    </div>
  </div>
  
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Sun className="w-5 h-5 text-orange-600" />
        <h3 className="font-semibold text-orange-800">פתיחת יום</h3>
      </div>
      <p className="text-sm text-orange-700">
        תכנן את היום: יעדים, משימות מרכזיות, פגישות וחסמים צפויים
      </p>
    </div>
    
    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Moon className="w-5 h-5 text-purple-600" />
        <h3 className="font-semibold text-purple-800">סיכום יום</h3>
      </div>
      <p className="text-sm text-purple-700">
        סכם את היום: הישגים, אתגרים, תובנות ותכנון למחר
      </p>
    </div>
  </div>
</div>
            

            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">פעולות מהירות</h3>
              <div className="flex flex-wrap gap-3">
                <button 
                 onClick={() => setShowNewProjectModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <FolderOpen size={16} />
                  פרויקט חדש
                </button>
                <button 
                 onClick={() => setShowNewTaskModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <CheckSquare size={16} />
                  משימה חדשה
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    };

    return [managementTab, personalTab];
  })();

  return (
    <div className="p-6">
      <TabsComponent tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      

      
      {/* Modals */}
      {/* Add Team Member Modal */}
      {showAddTeamMemberModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowAddTeamMemberModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowAddTeamMemberModal(false)}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h2 className="text-xl font-semibold text-white">הוסף עובד לצוות</h2>
                <p className="text-blue-100 text-sm">בחר עובד והוסף אותו לצוות שלי</p>
              </div>
              <div className="w-8 h-8"></div>
            </div>
            
            {/* Form */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  בחר עובד מהמחלקה
                </label>
                <select
                  value={selectedUserToAdd}
                  onChange={(e) => setSelectedUserToAdd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="">בחר עובד...</option>
                  {users.filter(u => 
                    u.departmentId === currentUser?.departmentId && 
                    u.role === 'employee' && 
                    u.isActive && 
                    !u.teamId
                  ).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email} 
                      {user.teamId ? ' (כבר בצוות)' : ' (פנוי)'}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  רק עובדים מהמחלקה שלך שלא משויכים לצוות אחר
                </p>
              </div>

              {/* Team Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-800">מידע על הצוות</span>
                </div>
                <div className="text-sm text-blue-700 space-y-1">
                  <div>• העובד יוקצה לצוות שלך במחלקת {departments.find(d => d.id === currentUser?.departmentId)?.name || 'לא ידוע'}</div>
                  <div>• העובד יישאר במחלקה הנוכחית שלו</div>
                  <div>• העובד יוכל לעבוד על משימות ופרויקטים של הצוות</div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowAddTeamMemberModal(false)}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button
                  onClick={handleAddTeamMember}
                  disabled={!selectedUserToAdd || (!addToAllDepartments && !selectedDepartmentId)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  הוסף לצוות
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowNewProjectModal(false);
            setShowNewProject(false);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowNewProjectModal(false);
                  setShowNewProject(false);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h2 className="text-xl font-semibold text-white">פרויקט חדש במחלקה</h2>
                <p className="text-blue-100 text-sm">צור פרויקט חדש במחלקה שלך</p>
              </div>
              <div className="w-8 h-8"></div>
            </div>
            
            {/* Form */}
            <form onSubmit={handleCreateProject} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      שם הפרויקט
                    </label>
                    <input
                      type="text"
                      value={newProject.name}
                      onChange={(e) => setNewProject(prev => ({...prev, name: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="הכנס שם לפרויקט..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור הפרויקט
                    </label>
                    <textarea
                      value={newProject.description}
                      onChange={(e) => setNewProject(prev => ({...prev, description: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none"
                      placeholder="תאר את הפרויקט בפירוט..."
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-500" />
                      תחום
                    </label>
                    <select
                      value={newProject.scope}
                      onChange={(e) => setNewProject(prev => ({...prev, scope: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="department">מחלקה</option>
                      <option value="personal">אישי</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-gray-500" />
                      עדיפות
                    </label>
                    <select
                      value={newProject.priority}
                      onChange={(e) => setNewProject(prev => ({...prev, priority: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="important_urgent">חשוב ודחוף</option>
                      <option value="important_not_urgent">חשוב ולא דחוף</option>
                      <option value="not_important_urgent">דחוף ולא חשוב</option>
                      <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      תאריך התחלה
                    </label>
                    <input
                      type="date"
                      value={newProject.startDate}
                      onChange={(e) => setNewProject(prev => ({...prev, startDate: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      תאריך סיום
                    </label>
                    <input
                      type="date"
                      value={newProject.endDate}
                      onChange={(e) => setNewProject(prev => ({...prev, endDate: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>

                  {/* Team Members Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      חברי צוות
                    </label>
                    <select
                      multiple
                      value={newProject.teamMembers || []}
                      onChange={(e) => {
                        const values = Array.from(e.target.selectedOptions).map(o => o.value);
                        setNewProject(prev => ({ ...prev, teamMembers: values }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all min-h-[120px]"
                    >
                      {users
                        .filter(u => u.role === 'employee' && u.isActive)
                        .filter(u => u.departmentId === currentUser.departmentId)
                        .slice()
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name || user.email}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">החזק Ctrl/Command לבחירה מרובה</p>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewProjectModal(false);
                    setShowNewProject(false);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  צור פרויקט
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowNewTaskModal(false);
            setShowNewTask(false);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-purple-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowNewTaskModal(false);
                  setShowNewTask(false);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h2 className="text-xl font-semibold text-white">משימה חדשה במחלקה</h2>
                <p className="text-purple-100 text-sm">צור משימה חדשה במחלקה שלך</p>
              </div>
              <div className="w-8 h-8"></div>
            </div>
            
            {/* Form */}
            <form onSubmit={handleCreateTask} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <CheckSquare className="w-4 h-4 text-gray-500" />
                      כותרת המשימה
                    </label>
                    <input
                      type="text"
                      value={newTask.title}
                      onChange={(e) => setNewTask(prev => ({...prev, title: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="הכנס כותרת למשימה..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      פרויקט
                    </label>
                    <select
                      value={newTask.projectId || ''}
                      onChange={(e) => setNewTask(prev => ({...prev, projectId: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">בחר פרויקט (אופציונלי)...</option>
                      {departmentProjects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור המשימה
                    </label>
                    <textarea
                      value={newTask.description}
                      onChange={(e) => setNewTask(prev => ({...prev, description: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none"
                      placeholder="תאר את המשימה בפירוט..."
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-gray-500" />
                      עדיפות
                    </label>
                    <select
                      value={newTask.priority}
                      onChange={(e) => setNewTask(prev => ({...prev, priority: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="important_urgent">חשוב ודחוף</option>
                      <option value="important_not_urgent">חשוב ולא דחוף</option>
                      <option value="not_important_urgent">דחוף ולא חשוב</option>
                      <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      מוקצה ל
                    </label>
                    <select
                      value={newTask.assigneeId}
                      onChange={(e) => setNewTask(prev => ({...prev, assigneeId: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">בחר עובד...</option>
                      {users.filter(u => 
                        u.departmentId === currentUser.departmentId && u.id !== currentUser.id
                      ).map(member => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      תאריך יעד
                    </label>
                    <input
                      type="date"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask(prev => ({...prev, dueDate: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      הערכת שעות
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={newTask.estimate}
                      onChange={(e) => setNewTask(prev => ({...prev, estimate: parseFloat(e.target.value) || 0}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewTaskModal(false);
                    setShowNewTask(false);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  צור משימה
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditProject && editProject && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditProject(false);
            setEditProject(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditProject(false);
                  setEditProject(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך פרויקט</h3>
                <p className="text-blue-100 text-sm">ערוך את פרטי הפרויקט</p>
              </div>
              <button
                type="submit"
                form="editProjectForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editProjectForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateProject(editProject.id, editProject);
                setShowEditProject(false);
                setEditProject(null);
              } catch (error) {
                console.error('Error updating project:', error);
              }
            }} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-gray-500" />
                      שם הפרויקט
                    </label>
                    <input 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                      placeholder="שם הפרויקט" 
                      value={editProject.name} 
                      onChange={(e) => setEditProject({...editProject, name: e.target.value})} 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור הפרויקט
                    </label>
                    <textarea 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all h-24 resize-none" 
                      placeholder="תיאור הפרויקט" 
                      value={editProject.description} 
                      onChange={(e) => setEditProject({...editProject, description: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-500" />
                      תחום
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={editProject.scope}
                      onChange={(e) => setEditProject({...editProject, scope: e.target.value})}
                    >
                      <option value="personal">אישי</option>
                      <option value="department">מחלקה</option>
                      <option value="organization">ארגון</option>
                    </select>
                  </div>
                </div>
                
                {/* Right Column - Additional Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      בחר מחלקה
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      value={editProject.departmentId}
                      onChange={(e) => setEditProject({...editProject, departmentId: e.target.value})}
                    >
                      <option value="">בחר מחלקה</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        תאריך התחלה
                      </label>
                      <input 
                        type="date" 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                        placeholder="תאריך התחלה" 
                        value={editProject.startDate} 
                        onChange={(e) => setEditProject({...editProject, startDate: e.target.value})} 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        תאריך סיום
                      </label>
                      <input 
                        type="date" 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                        placeholder="תאריך סיום" 
                        value={editProject.endDate} 
                        onChange={(e) => setEditProject({...editProject, endDate: e.target.value})} 
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditProject(false);
                    setEditProject(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditTask && editTask && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditTask(false);
            setEditTask(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-purple-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditTask(false);
                  setEditTask(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך משימה</h3>
                <p className="text-purple-100 text-sm">ערוך את פרטי המשימה</p>
              </div>
              <button
                type="submit"
                form="editTaskForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editTaskForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateTask(editTask.id, editTask);
                setShowEditTask(false);
                setEditTask(null);
              } catch (error) {
                console.error('Error updating task:', error);
              }
            }} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      כותרת המשימה
                    </label>
                    <input 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="כותרת המשימה" 
                      value={editTask.title} 
                      onChange={(e) => setEditTask({...editTask, title: e.target.value})} 
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      תיאור המשימה
                    </label>
                    <textarea 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all h-24 resize-none" 
                      placeholder="תיאור המשימה" 
                      value={editTask.description} 
                      onChange={(e) => setEditTask({...editTask, description: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-gray-500" />
                      עדיפות
                    </label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                      value={editTask.priority}
                      onChange={(e) => setEditTask({...editTask, priority: e.target.value})}
                    >
                      <option value="important_urgent">חשוב ודחוף</option>
                      <option value="important_not_urgent">חשוב ולא דחוף</option>
                      <option value="not_important_urgent">דחוף ולא חשוב</option>
                      <option value="not_important_not_urgent">לא חשוב ולא דחוף</option>
                    </select>
                  </div>
                </div>
                
                {/* Right Column - Additional Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      דדליין
                    </label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="דדליין" 
                      value={editTask.dueDate} 
                      onChange={(e) => setEditTask({...editTask, dueDate: e.target.value})} 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      הערכת שעות
                    </label>
                    <input 
                      type="number" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                      placeholder="הערכת שעות" 
                      value={editTask.estimate} 
                      onChange={(e) => setEditTask({...editTask, estimate: parseInt(e.target.value) || 0})} 
                    />
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditTask(false);
                    setEditTask(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" 
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Team Modal */}
      {showNewTeam && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowNewTeam(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-orange-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setShowNewTeam(false)}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">צור צוות חדש</h3>
                <p className="text-orange-100 text-sm">הוסף צוות חדש למחלקה שלך</p>
              </div>
              <button
                type="submit"
                form="newTeamForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Plus className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="newTeamForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createTeam(newTeam);
                setShowNewTeam(false);
                setNewTeam({
                  name: '',
                  description: '',
                  departmentId: currentUser?.departmentId || '',
                  managerId: '',
                  memberIds: [],
                  isActive: true
                });
              } catch (error) {
                console.error('Error creating team:', error);
              }
            }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות</label>
                  <input 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" 
                    placeholder="שם הצוות" 
                    value={newTeam.name} 
                    onChange={(e) => setNewTeam({...newTeam, name: e.target.value})} 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                  <textarea 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 h-24 resize-none" 
                    placeholder="תיאור הצוות" 
                    value={newTeam.description} 
                    onChange={(e) => setNewTeam({...newTeam, description: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">מנהל צוות</label>
                  <select 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    value={newTeam.managerId}
                    onChange={(e) => setNewTeam({...newTeam, managerId: e.target.value})}
                  >
                    <option value="">בחר מנהל</option>
                    {users.filter(u => u.departmentId === currentUser?.departmentId).map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">חברי צוות</label>
                  <select 
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 h-32"
                    value={newTeam.memberIds}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setNewTeam({...newTeam, memberIds: selectedOptions});
                    }}
                  >
                    {users.filter(u => u.departmentId === currentUser?.departmentId).map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">החזק Ctrl (או Cmd) לבחירה מרובה</p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="teamActive"
                    checked={newTeam.isActive}
                    onChange={(e) => setNewTeam({...newTeam, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="teamActive" className="text-sm text-gray-700">צוות פעיל</label>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => setShowNewTeam(false)}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  צור צוות
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditTeam && editTeam && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowEditTeam(false);
            setEditTeam(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowEditTeam(false);
                  setEditTeam(null);
                }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white">ערוך צוות</h3>
                <p className="text-blue-100 text-sm">ערוך את פרטי הצוות</p>
              </div>
              <button
                type="submit"
                form="editTeamForm"
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Form */}
            <form id="editTeamForm" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateTeam(editTeam.id, editTeam);
                setShowEditTeam(false);
                setEditTeam(null);
              } catch (error) {
                console.error('Error updating team:', error);
              }
            }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">שם הצוות</label>
                  <input 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                    placeholder="שם הצוות" 
                    value={editTeam.name} 
                    onChange={(e) => setEditTeam({...editTeam, name: e.target.value})} 
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">תיאור</label>
                  <textarea 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24 resize-none" 
                    placeholder="תיאור הצוות" 
                    value={editTeam.description} 
                    onChange={(e) => setEditTeam({...editTeam, description: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">מנהל צוות</label>
                  <select 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={editTeam.managerId}
                    onChange={(e) => setEditTeam({...editTeam, managerId: e.target.value})}
                  >
                    <option value="">בחר מנהל</option>
                    {users.filter(u => u.departmentId === currentUser?.departmentId).map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">חברי צוות</label>
                  <select 
                    multiple
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
                    value={editTeam.memberIds || []}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setEditTeam({...editTeam, memberIds: selectedOptions});
                    }}
                  >
                    {users.filter(u => u.departmentId === currentUser?.departmentId).map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">החזק Ctrl (או Cmd) לבחירה מרובה</p>
                </div>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="editTeamActive"
                    checked={editTeam.isActive}
                    onChange={(e) => setEditTeam({...editTeam, isActive: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="editTeamActive" className="text-sm text-gray-700">צוות פעיל</label>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowEditTeam(false);
                    setEditTeam(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  ביטול
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  שמור שינויים
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeptManagerDashboard;